import {
  PieceAnnotation,
  TrackingNumberAnnouncementStatus,
  TrackingNumberValidationStatus,
  Workflow,
} from '@apiContract';

import { getValidationStatuses } from '@utils/annotations';

enum Alert {
  NoTracking = 'No tracking numbers were identified',
  Identified = 'Tracking number(s) identified successfully',
  LockersIdentified = 'Tracking number(s) identified and sent to lockers successfully',
  UnableTracking = 'Some tracking numbers were unable to be sent to lockers',
  NotUnique = 'This item does not have any unique tracking numbers',
  Duplicate = 'This item looks like it was already received. Please delete it or its duplicate',
  MustBeAssigned = 'Item must be assigned to send tracking number(s) to lockers',
}

enum Severity {
  Success = 'success',
  Error = 'error',
  Info = 'info',
}

const tooltips = {
  barcode: 'Reading barcodes.',
  lockers: 'Sending to lockers.',
};

export const getAlert = (annotations: Array<PieceAnnotation>) => {
  const { validation } = getValidationStatuses(annotations);

  const validCount = validation.get(TrackingNumberValidationStatus.Valid) || 0;

  if (validCount > 0) {
    return { text: Alert.Identified, severity: Severity.Success };
  }

  return;
};

const checkForConfirmedItems = (annotations: Array<PieceAnnotation>): boolean => {
  const { announce } = getValidationStatuses(annotations);
  const confirmedCount = announce.get(TrackingNumberAnnouncementStatus.Confirmed) || 0;
  return confirmedCount > 0;
};

const getStatusCounts = (filtered: Array<PieceAnnotation>) => {
  const { validation, announce } = getValidationStatuses(filtered);

  return {
    sent: announce.get(TrackingNumberAnnouncementStatus.Sent) || 0,
    failed: announce.get(TrackingNumberAnnouncementStatus.Failed) || 0,
    badFormat: validation.get(TrackingNumberValidationStatus.BadFormat) || 0,
    conflict: validation.get(TrackingNumberValidationStatus.Conflict) || 0,
    allAreNone: filtered.every(
      (a) => a.pieceAnnotationsTrackingNumber?.announcementStatus === TrackingNumberAnnouncementStatus.None,
    ),
    hasAnyNone: filtered.some(
      (a) => a.pieceAnnotationsTrackingNumber?.announcementStatus === TrackingNumberAnnouncementStatus.None,
    ),
  };
};

const isAllInvalidFormat = (
  filtered: Array<PieceAnnotation>,
  statusCounts: ReturnType<typeof getStatusCounts>,
): boolean => {
  return statusCounts.conflict + statusCounts.badFormat === filtered.length;
};

export const getLockersAlert = (
  annotations: Array<PieceAnnotation> | null,
  filtered: Array<PieceAnnotation>,
  hasRecipient: boolean,
) => {
  if (!annotations?.length) return;

  const hasConfirmedItems = checkForConfirmedItems(annotations);

  if (hasConfirmedItems) {
    return { text: Alert.LockersIdentified, severity: Severity.Success };
  }

  if (!filtered.length) {
    return { text: Alert.NoTracking, severity: Severity.Error };
  }

  const statusCounts = getStatusCounts(filtered);

  if (statusCounts.sent > 0) {
    return;
  }

  if (statusCounts.allAreNone && !hasRecipient) {
    return { text: Alert.MustBeAssigned, severity: Severity.Info };
  }

  if (isAllInvalidFormat(filtered, statusCounts)) {
    return { text: Alert.NotUnique, severity: Severity.Error };
  }

  if (statusCounts.failed > 0 && !statusCounts.hasAnyNone) {
    return { text: Alert.UnableTracking, severity: Severity.Error };
  }

  return;
};

export const getTooltip = (workflow: Workflow, annotation?: PieceAnnotation, hasRecipient?: boolean) => {
  if (!annotation) {
    return tooltips.barcode;
  }
  if (workflow === Workflow.Lockers) {
    const announcementStatus = annotation.pieceAnnotationsTrackingNumber?.announcementStatus;
    const validationStatus = annotation.pieceAnnotationsTrackingNumber?.validationStatus;

    if (
      hasRecipient &&
      validationStatus === TrackingNumberValidationStatus.Valid &&
      (announcementStatus === TrackingNumberAnnouncementStatus.None ||
        announcementStatus === TrackingNumberAnnouncementStatus.Sent)
    ) {
      return tooltips.lockers;
    }
    return null;
  }
};
